import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Delete } from '@material-ui/icons';
import { format, sub } from 'date-fns';

import { UnidataTable } from '~/components';
import { DialogConfirmation } from '~/componentsLocal';
import {
  MODULO_20_GRAUS,
  MOTORISTA_ABASTECIMENTO,
  LOCALIDADE_ABASTECIMENTO,
} from '~/constants/modules';
import { t, number, can } from '~/helpers';
import { formatISO } from '~/helpers/dateTime';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  deleteOperacaoTransferencia,
  findOperacaoTransferencia,
  recordFiltering,
} from '~/store/modules/operacaotransferencia/actions';

import FormTransfer from './form';
import FormDeletar from './formDeletar';

function OperacaoTransferenciaList() {
  const redux = useSelector((state) => state.operacaotransferencia);
  const { internounidatausuario } = useSelector((state) => state.user.configs);

  const deleteRef = useRef(null);

  const dateTimes = useMemo(
    () => ({
      start: format(new Date(), 'dd/MM/yyyy 00:00:00'),
      end: format(new Date(), 'dd/MM/yyyy 23:59:59'),
    }),
    []
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'datahorainicialmovimentacao',
        label: t('s.operacao.transferencia.datahorainicialmovimentacao'),
        formatted: (date) => formatISO({ date }),
        filter: {
          type: 'initialdatetime',
          defaultValue: dateTimes.start,
        },
      },
      {
        id: 'datahorafinalmovimentacao',
        label: t('s.operacao.transferencia.datahorafinalmovimentacao'),
        formatted: (date) => formatISO({ date }),
        filter: {
          type: 'finaldatetime',
          defaultValue: dateTimes.end,
        },
      },
      {
        id: 'codigonomeposto',
        label: t('s.geral.pontoorigem'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'idbico',
        label: t('s.operacao.transferencia.idbico'),
        filter: {
          type: 'number',
        },
        align: 'left',
      },
      {
        id: 'nomeproduto',
        label: t('s.operacao.transferencia.nomeproduto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigonomepostodestino',
        label: t('s.geral.pontodestino'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigousuario',
        label: t('s.operacao.transferencia.codigousuario'),
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'encerrantemovimentacaotransferencia',
        label: t('s.relatorio.vazaonaoautorizadalistagem.encerrante'),
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'volumemovimentacao',
        label: t('s.operacao.transferencia.volumemovimentacao'),
        align: 'right',
        formatted: (value) => number(value, 2),
      },
    ];

    if (can(MODULO_20_GRAUS)) {
      data.splice(9, 0, {
        id: 'densidade20movimentacao',
        label: t('s.operacao.abastecimento.densidade20'),
        align: 'right',
        formatted: (value) => number(value, 4),
      });

      data.splice(9, 0, {
        id: 'temperaturamediamovimentacao',
        label: t('s.operacao.abastecimento.temperaturamedia'),
        align: 'right',
        formatted: (value) => number(value, 1),
      });

      data.splice(9, 0, {
        id: 'volume20movimentacao',
        label: t('s.operacao.abastecimento.volume20movimentacao'),
        align: 'right',
        formatted: (value) => number(value, 2),
      });
    }

    return data;
  }, [dateTimes]);

  const actions = useMemo(() => {
    if (internounidatausuario === 'S') {
      return [
        {
          title: t('s.geral.botaoexcluir'),
          action: (value) => deleteRef.current.handleOpen(value),
          icon: <Delete />,
        },
      ];
    }

    return [];
  }, [internounidatausuario]);

  const fieldlist = [
    'datahorainicialmovimentacao',
    'datahorafinalmovimentacao',
    'codigonomeposto',
    'idbico',
    'nomeproduto',
    'codigonomepostodestino',
    'codigousuario',
    'encerrantemovimentacaotransferencia',
    'volumemovimentacao',
  ];

  const substituirHeader = {
    datahorainicialmovimentacao: t(
      's.operacao.transferencia.datahorainicialmovimentacao'
    ),
    datahorafinalmovimentacao: t(
      's.operacao.transferencia.datahorafinalmovimentacao'
    ),
    codigonomeposto: t('s.geral.pontoorigem'),
    idbico: t('s.operacao.transferencia.idbico'),
    nomeproduto: t('s.operacao.transferencia.nomeproduto'),
    codigonomepostodestino: t('s.geral.pontodestino'),
    codigousuario: t('s.operacao.transferencia.codigousuario'),
    encerrantemovimentacaotransferencia: t(
      's.relatorio.vazaonaoautorizadalistagem.encerrante'
    ),
    volumemovimentacao: t('s.operacao.transferencia.volumemovimentacao'),
  };

  if (can(LOCALIDADE_ABASTECIMENTO)) {
    fieldlist.push('idlocalidade');
    substituirHeader.idlocalidade = t('s.cadastro.localidade');
  }

  if (can(MOTORISTA_ABASTECIMENTO)) {
    fieldlist.push('idmotorista');
    substituirHeader.idmotorista = t('s.cadastro.motorista');
  }

  if (can(MODULO_20_GRAUS)) {
    fieldlist.push('temperaturamediamovimentacao');
    fieldlist.push('densidade20movimentacao');
    fieldlist.push('volume20movimentacao');
    substituirHeader.temperaturamediamovimentacao = t(
      's.operacao.abastecimento.temperaturamedia'
    );
    substituirHeader.densidade20movimentacao = t(
      's.operacao.abastecimento.densidade20'
    );
    substituirHeader.volume20movimentacao = t(
      's.operacao.abastecimento.volume20movimentacao'
    );
  }

  return (
    <>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findOperacaoTransferencia}
        delete={internounidatausuario !== 'S'}
        remove={deleteOperacaoTransferencia}
        form={FormTransfer}
        formAux={FormDeletar}
        actions={actions}
        detach="nomeposto"
        keys={['idmovimentacao', 'idposto']}
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'movimentacoestransferencias',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatório de Transferências',
          })
        }
      />

      <DialogConfirmation
        ref={deleteRef}
        keys={['idmovimentacao', 'idposto']}
        title="Justificativa para exclusão de registro"
        redux={deleteOperacaoTransferencia}
        query={redux.params}
      />
    </>
  );
}

export default OperacaoTransferenciaList;
